import { useEffect, useMemo, useState } from 'react';
import { plannerAPI } from '../../api';
import { Button } from '../ui/Button';
import { Bell, ClipboardList } from 'lucide-react';

const COLOR_CLASSES = {
  blue: 'bg-blue-500',
  red: 'bg-red-500',
  green: 'bg-emerald-500',
  yellow: 'bg-amber-500',
  purple: 'bg-violet-500',
  orange: 'bg-orange-500',
  gray: 'bg-gray-500',
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatRelative = (value) => {
  if (!value) return '';
  const date = new Date(value);
  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / 60000);
  if (Math.abs(diffMinutes) < 1) return 'now';
  if (diffMinutes > 0 && diffMinutes < 60) return `in ${diffMinutes}m`;
  if (diffMinutes < 0 && diffMinutes > -60) return `${Math.abs(diffMinutes)}m ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24) {
    return diffHours > 0 ? `in ${diffHours}h` : `${Math.abs(diffHours)}h ago`;
  }
  const diffDays = Math.round(diffHours / 24);
  return diffDays > 0 ? `in ${diffDays}d` : `${Math.abs(diffDays)}d ago`;
};

const formatAssigner = (item) => {
  const first = item.assigned_by_first_name || '';
  const last = item.assigned_by_last_name || '';
  const name = [first, last].filter(Boolean).join(' ');
  return name || item.assigned_by_email || 'Team member';
};

export default function DashboardNotificationsBar() {
  const [reminders, setReminders] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(true);
  const [loadingAssignments, setLoadingAssignments] = useState(true);

  const loadReminders = async () => {
    try {
      const data = await plannerAPI.getUpcomingReminders({ limit: 20 });
      setReminders(data.reminders || []);
    } catch (error) {
      console.error('Failed to load reminders', error);
    } finally {
      setLoadingReminders(false);
    }
  };

  const loadAssignments = async () => {
    try {
      const data = await plannerAPI.getAssignedToMe({ status: 'pending' });
      setAssignments(data.items || []);
    } catch (error) {
      console.error('Failed to load assignments', error);
    } finally {
      setLoadingAssignments(false);
    }
  };

  useEffect(() => {
    loadReminders();
    loadAssignments();
    const t = 5 * 60 * 1000;
    const tr = setInterval(loadReminders, t);
    const ta = setInterval(loadAssignments, t);
    return () => {
      clearInterval(tr);
      clearInterval(ta);
    };
  }, []);

  const handleDismissReminder = async (reminderId) => {
    try {
      await plannerAPI.dismissReminder(reminderId);
      setReminders((prev) => prev.filter((r) => r.id !== reminderId));
    } catch (error) {
      console.error('Failed to dismiss reminder', error);
    }
  };

  const handleMarkDone = async (plannerItemId) => {
    try {
      await plannerAPI.toggleDone(plannerItemId);
      setReminders((prev) => prev.filter((r) => r.planner_item_id !== plannerItemId));
    } catch (error) {
      console.error('Failed to mark task done', error);
    }
  };

  const handleAccept = async (id) => {
    try {
      await plannerAPI.acceptAssignment(id);
      setAssignments((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error('Failed to accept assignment', error);
    }
  };

  const handleReject = async (id) => {
    try {
      await plannerAPI.rejectAssignment(id);
      setAssignments((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error('Failed to reject assignment', error);
    }
  };

  const loading = loadingReminders || loadingAssignments;
  const hasReminders = reminders.length > 0;
  const hasAssignments = assignments.length > 0;
  const hasAny = hasReminders || hasAssignments;

  const relativeCache = useMemo(
    () => new Map(reminders.map((r) => [r.id, formatRelative(r.remind_at)])),
    [reminders]
  );

  if (loading || !hasAny) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 py-3 text-sm border-b border-[var(--ds-border-subtle)] mb-6">
      {hasReminders && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-[var(--ds-text-secondary)] font-medium shrink-0">
            <Bell className="h-4 w-4" />
            Reminders
          </span>
          {reminders.map((reminder) => {
            const colorClass = COLOR_CLASSES[reminder.color] || COLOR_CLASSES.blue;
            return (
              <div
                key={reminder.id}
                className="inline-flex items-center gap-2 flex-wrap rounded-md px-2.5 py-1.5 bg-[var(--ds-control-field-bg)] border border-[var(--ds-border-subtle)]"
              >
                <span className={`h-2 w-2 rounded-full shrink-0 ${colorClass}`} />
                <span className="text-[var(--ds-text-primary)] font-medium">{reminder.title}</span>
                <span className="text-[var(--ds-text-muted)]">
                  {formatDateTime(reminder.start_at)} · {relativeCache.get(reminder.id)}
                </span>
                <span className="flex items-center gap-1">
                  {reminder.item_type === 'task' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs"
                      onClick={() => handleMarkDone(reminder.planner_item_id)}
                    >
                      Done
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={() => handleDismissReminder(reminder.id)}
                  >
                    Dismiss
                  </Button>
                </span>
              </div>
            );
          })}
        </div>
      )}
      {hasAssignments && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-[var(--ds-text-secondary)] font-medium shrink-0">
            <ClipboardList className="h-4 w-4" />
            Pending
          </span>
          {assignments.map((assignment) => (
            <div
              key={assignment.id}
              className="inline-flex items-center gap-2 flex-wrap rounded-md px-2.5 py-1.5 bg-[var(--ds-control-field-bg)] border border-[var(--ds-border-subtle)]"
            >
              <span className="text-[var(--ds-text-primary)] font-medium">{assignment.title}</span>
              <span className="text-[var(--ds-text-muted)]">
                {formatAssigner(assignment)} · {formatDateTime(assignment.start_at)}
              </span>
              <span className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  onClick={() => handleReject(assignment.id)}
                >
                  Reject
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  className="h-7 px-2 text-xs"
                  onClick={() => handleAccept(assignment.id)}
                >
                  Accept
                </Button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
